import { decimalAdjust } from '@/features/bidding/utils/calculations/decimalAdjust'
import sumArray from '@/features/bidding/utils/calculations/sumArray'
import type { ParsedPriceBid } from '@/features/bidding/utils/clipboard'
import { PriceLadderType } from '@/features/bidding/utils/getBidConfig'
import type { AggregatedBid, AggregatedBidPtu } from '@/features/bidding/utils/model/buildAggregatedBid'
import type { PriceQuantity } from '@/features/bidding/utils/price/getPtuPriceQuantitiesFromBids'

export type BidPriceQuantity = {
  price: number
  quantity: number
}

export const createAggregatedBidFromParsedBidAndRemoveCurrentPriceColumns = (
  parsedBid: ParsedPriceBid,
  aggregatedBid: AggregatedBid,
) => ({
  ...aggregatedBid,
  ptus: aggregatedBid.ptus.map((ptu, ptuIndex) => {
    const ptuVolumes = parsedBid?.volumes[ptuIndex]
    const priceQuantities =
      parsedBid?.prices.map((price, priceIndex) => ({
        price: price,
        quantity: ptuVolumes?.[priceIndex] ?? 0,
      })) ?? []

    const newPtu = {
      ...ptu,
      priceQuantities,
    }

    recalculateRemaining(newPtu, aggregatedBid.ladderType)
    return newPtu
  }),
})

export const createAggregatedBidFromParsedBidAndMakeVolumeInCurrentPriceColumnsAsZero = (
  parsedBid: ParsedPriceBid,
  aggregatedBid: AggregatedBid,
) => {
  const parsedBidPrices = parsedBid?.prices ?? []

  return {
    ...aggregatedBid,
    ptus: aggregatedBid.ptus.map((ptu, ptuIndex) => {
      const ptuVolumes = parsedBid?.volumes[ptuIndex]
      const currentPriceQuantitiesNotPresentAsPriceInTheParsedBid = ptu.priceQuantities
        .filter((priceQuantity: BidPriceQuantity) => !parsedBidPrices.includes(priceQuantity.price))
        .map((priceQuantity: BidPriceQuantity) => ({
          price: priceQuantity.price,
          quantity: 0,
        }))

      const priceQuantitiesInTheParsedBid: BidPriceQuantity[] = parsedBidPrices.map((price, priceIndex) => ({
        price: price,
        quantity: ptuVolumes?.[priceIndex] ?? 0,
      }))

      const combinedPriceQuantities: BidPriceQuantity[] = [
        ...currentPriceQuantitiesNotPresentAsPriceInTheParsedBid,
        ...priceQuantitiesInTheParsedBid,
      ]
      const newPtu = {
        ...ptu,
        priceQuantities: combinedPriceQuantities,
      }

      recalculateRemaining(newPtu, aggregatedBid.ladderType)
      return newPtu
    }),
  }
}

export const recalculateRemaining = (updatedPtu: AggregatedBidPtu, ladderType: PriceLadderType) => {
  // if the user has changed the quantity for one price, the total remaining quantity must be recalculated
  const offered = calculateTotalOffered(updatedPtu.priceQuantities, ladderType)
  updatedPtu.remaining = decimalAdjust('round', updatedPtu.available - offered, -2)
  return updatedPtu
}

export const calculateTotalOffered = (priceQuantities: PriceQuantity[], priceLadderType: PriceLadderType) => {
  // A PTU can legitimately carry no price steps at all (every column can be removed via the
  // "Set price columns" dialog), which means nothing is offered for it. The incremental branch
  // already answers 0 through sumArray([]); the cumulative branch indexed into the sorted ladder
  // and threw, crashing the page for GR, the only market on a cumulative ladder.
  if (priceQuantities.length === 0) {
    return 0
  }

  if (priceLadderType === PriceLadderType.INCREMENTAL) {
    return sumArray(priceQuantities.map((priceQuantity) => priceQuantity.quantity))
  } else {
    return priceQuantities.toSorted((a, b) => b.quantity - a.quantity)[0].quantity
  }
}

export const getUniquePrices = (bidPtus: AggregatedBidPtu[]) => {
  const prices = bidPtus.flatMap((rowItem) => rowItem.priceQuantities.map((priceQuantity) => priceQuantity.price))
  return Array.from(new Set(prices)).sort((a, b) => a - b)
}
